import {
  collection, getDocs, addDoc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Payroll service — monthly payroll for restaurants and delivery partners.
 *
 * Period: a calendar month. Only DELIVERED orders are counted.
 * Mirrors the Flutter admin app:
 *  - Restaurant net = gross − commission − restaurant-funded promo.
 *  - Delivery payable = salary + surge earned + night-order bonus (₹50/order
 *    placed after 21:30).
 *  - Payments accumulate (partial payouts) toward the total; an entity is PAID
 *    once paid ≥ total.
 * Recording a payout writes a doc to `zapee_transactions`.
 */

const ORDERS_COLLECTION = 'zapee_restaurant_orders';
const RESTAURANT_COLLECTION = 'zapee_restaurant_partner';
const PARTNER_COLLECTION = 'zapee_delivery_partners';
const TXN_COLLECTION = 'zapee_transactions';

const DEFAULT_COMMISSION_PCT = 20;
export const NIGHT_BONUS_PER_ORDER = 50;
const NIGHT_CUTOFF_MIN = 21 * 60 + 30; // 21:30

// ── Types ────────────────────────────────────────────────────────
export interface OrderLite {
  id: string;
  createdAt: Date | null;
  deliveredAt: Date | null;
  grandTotal: number;       // total the customer was billed
  productAmount: number;    // items subtotal (charges stripped)
  deliveryCharge: number;
  surge: number;            // surge bonus — the partner's earning
  platformFee: number;
  packing: number;
  customerPaid: number;     // cash actually collected (COD)
  paymentMethod: string;
  isCash: boolean;
  isNight: boolean;         // placed after 21:30
  freeDelivery: boolean;
  freeDeliveryAmount: number;
  offerDiscount: number;
  restaurantPromo: number;  // restaurant-funded free delivery + offers
}

export interface RestaurantPayrollRow {
  entityId: string;          // sellerId
  name: string;
  gross: number;
  commissionPct: number;
  commission: number;
  freeDeliveryDeduction: number;
  offerDeduction: number;
  promoDeduction: number;    // free delivery + offers (restaurant-funded)
  totalDeduction: number;    // commission + promo
  net: number;
  orderCount: number;
  paidSoFar: number;
  remaining: number;
  isPaid: boolean;
  orders: OrderLite[];
}

export interface DPPayrollRow {
  entityId: string;          // delivery partner doc id
  name: string;
  salary: number;            // fixed monthly base
  deliveredCount: number;
  surge: number;             // surge earned this month (extra pay)
  nightCount: number;        // orders placed after 21:30
  nightBonus: number;        // nightCount × ₹50
  payable: number;           // salary + surge + nightBonus
  paidSoFar: number;
  remaining: number;
  isPaid: boolean;
  orders: OrderLite[];
}

export interface Txn {
  id: string;
  type: 'restaurant' | 'partner';
  entityId: string;
  entityName: string;
  amount: number;
  gross?: number;
  deductions?: number;
  orderCount?: number;
  periodMonth: number;
  periodYear: number;
  paidAt?: any;
  status?: string;
  referenceId?: string;
  paymentMethod?: string;
  notes?: string;
}

export interface RecordPayoutArgs {
  type: 'restaurant' | 'partner';
  entityId: string;
  entityName: string;
  amount: number;
  gross?: number;
  deductions?: number;
  orderCount?: number;
  periodMonth: number;
  periodYear: number;
  referenceId?: string;
  paymentMethod?: string;
  notes?: string;
}

// ── Helpers ──────────────────────────────────────────────────────
function toDate(ts: any): Date | null {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function num(v: any): number {
  return typeof v === 'number' ? v : (Number(v) || 0);
}

function orderDate(o: any): Date | null {
  return toDate(o.deliveredAt) ?? toDate(o.createdAt);
}

function isDeliveredInMonth(o: any, month: number, year: number): boolean {
  if ((o.status || '').toUpperCase() !== 'DELIVERED') return false;
  const d = orderDate(o);
  if (!d) return false;
  return d.getFullYear() === year && d.getMonth() === month;
}

// Restaurant-funded promo for one order (free delivery + offer discount).
function restaurantPromoDeduction(o: any): number {
  const promo = o.promo;
  if (!promo) return 0;
  const funded = promo.fundedBy ?? promo.appliedOffer?.fundedBy;
  if (funded !== 'restaurant') return 0;
  let d = 0;
  if (promo.freeDelivery) d += num(promo.freeDeliveryAmount);
  d += num(promo.appliedOffer?.discountAmount ?? promo.discountAmount);
  return d;
}

// Parse a raw order doc into the lightweight shape used by the screens.
function parseOrder(id: string, o: any): OrderLite {
  const ch = o.charges || {};
  const deliveryCharge = num(ch.deliveryCharge ?? o.deliveryCharge);
  const packing = num(ch.packing ?? o.packing);
  const surge = num(ch.surge ?? o.surge);
  const platformFee = num(ch.platformFee ?? o.platformFee);
  const grandTotal = num(o.totalPrice ?? o.grandTotal ?? o.total);
  const productAmount = Math.max(0, grandTotal - (deliveryCharge + packing + surge + platformFee));
  const createdAt = toDate(o.createdAt);
  const mins = createdAt ? createdAt.getHours() * 60 + createdAt.getMinutes() : -1;
  const promo = o.promo || {};
  const funded = promo.fundedBy ?? promo.appliedOffer?.fundedBy;
  const pm = String(o.payment?.method ?? o.paymentMethod ?? 'COD').toUpperCase();
  return {
    id,
    createdAt,
    deliveredAt: toDate(o.deliveredAt),
    grandTotal,
    productAmount,
    deliveryCharge,
    surge,
    platformFee,
    packing,
    customerPaid: num(o.customerPaid ?? grandTotal),
    paymentMethod: pm,
    isCash: pm.includes('CASH') || pm.includes('COD'),
    isNight: mins >= NIGHT_CUTOFF_MIN,
    freeDelivery: !!promo.freeDelivery,
    freeDeliveryAmount: num(promo.freeDeliveryAmount),
    offerDiscount: num(promo.appliedOffer?.discountAmount ?? promo.discountAmount),
    restaurantPromo: restaurantPromoDeduction(o),
  };
}

// Sum payouts already recorded this month, grouped by entity, for one type.
async function paidByEntity(type: 'restaurant' | 'partner', month: number, year: number): Promise<Map<string, number>> {
  const q = query(
    collection(db, TXN_COLLECTION),
    where('periodMonth', '==', month),
    where('periodYear', '==', year),
  );
  const snap = await getDocs(q);
  const map = new Map<string, number>();
  snap.docs.forEach(d => {
    const data = d.data() as any;
    if ((data.type ?? 'restaurant') !== type) return;
    const key = String(data.entityId ?? (type === 'partner' ? data.partnerId : data.restaurantId) ?? '');
    if (!key) return;
    map.set(key, (map.get(key) || 0) + num(data.amount));
  });
  return map;
}

// ── Restaurant payroll ───────────────────────────────────────────
export async function getRestaurantPayroll(month: number, year: number): Promise<RestaurantPayrollRow[]> {
  const [ordersSnap, restaurantsSnap, paidMap] = await Promise.all([
    getDocs(collection(db, ORDERS_COLLECTION)),
    getDocs(collection(db, RESTAURANT_COLLECTION)),
    paidByEntity('restaurant', month, year),
  ]);

  const commissionById = new Map<string, number>();
  const storenameById = new Map<string, string>();
  restaurantsSnap.docs.forEach(d => {
    const data = d.data() as any;
    const pct = num(data.commission);
    const pctFinal = Number.isFinite(pct) && data.commission != null ? pct : DEFAULT_COMMISSION_PCT;
    const storename = data.storename || data.name || '';
    [data.storeuid, data.uid, d.id].forEach((key: any) => {
      if (key) {
        commissionById.set(String(key), pctFinal);
        storenameById.set(String(key), storename);
      }
    });
  });

  const groups = new Map<string, { gross: number; freeDel: number; offers: number; orderCount: number; name: string; orders: OrderLite[] }>();
  ordersSnap.docs.forEach(d => {
    const o = d.data() as any;
    if (!isDeliveredInMonth(o, month, year)) return;
    const sellerId = o.sellerId;
    if (!sellerId) return;
    const key = String(sellerId);
    const g = groups.get(key) || { gross: 0, freeDel: 0, offers: 0, orderCount: 0, name: '', orders: [] };
    const lite = parseOrder(d.id, o);
    g.gross += lite.productAmount;
    // Split the restaurant-funded promo into free-delivery vs offer for the breakdown.
    const promo = o.promo;
    const funded = promo?.fundedBy ?? promo?.appliedOffer?.fundedBy;
    if (funded === 'restaurant') {
      if (promo?.freeDelivery) g.freeDel += num(promo.freeDeliveryAmount);
      g.offers += num(promo?.appliedOffer?.discountAmount ?? promo?.discountAmount);
    }
    g.orderCount += 1;
    g.orders.push(lite);
    if (!g.name && o.sellerLocation?.name) g.name = o.sellerLocation.name;
    groups.set(key, g);
  });

  const rows: RestaurantPayrollRow[] = [];
  groups.forEach((g, entityId) => {
    const commissionPct = commissionById.has(entityId) ? commissionById.get(entityId)! : DEFAULT_COMMISSION_PCT;
    const commission = (g.gross * commissionPct) / 100;
    const promoDeduction = g.freeDel + g.offers;
    const totalDeduction = commission + promoDeduction;
    const net = Math.max(0, g.gross - totalDeduction);
    const name = g.name || storenameById.get(entityId) || entityId;
    const paidSoFar = paidMap.get(entityId) || 0;
    const remaining = Math.max(0, net - paidSoFar);
    const isPaid = net > 0 && remaining <= 0.5;
    rows.push({
      entityId, name, gross: g.gross, commissionPct, commission,
      freeDeliveryDeduction: g.freeDel, offerDeduction: g.offers, promoDeduction,
      totalDeduction, net, orderCount: g.orderCount, paidSoFar, remaining, isPaid,
      orders: g.orders,
    });
  });

  rows.sort((a, b) => b.remaining - a.remaining || b.net - a.net);
  return rows;
}

// ── Delivery-partner payroll ─────────────────────────────────────
export async function getDeliveryPayroll(month: number, year: number): Promise<DPPayrollRow[]> {
  const [partnersSnap, ordersSnap, paidMap] = await Promise.all([
    getDocs(collection(db, PARTNER_COLLECTION)),
    getDocs(collection(db, ORDERS_COLLECTION)),
    paidByEntity('partner', month, year),
  ]);

  const statsById = new Map<string, { count: number; surge: number; night: number; orders: OrderLite[] }>();
  ordersSnap.docs.forEach(d => {
    const o = d.data() as any;
    if (!isDeliveredInMonth(o, month, year)) return;
    const pid = o.deliveryPartnerId;
    if (!pid) return;
    const key = String(pid);
    const s = statsById.get(key) || { count: 0, surge: 0, night: 0, orders: [] };
    const lite = parseOrder(d.id, o);
    s.count += 1;
    s.surge += lite.surge;
    if (lite.isNight) s.night += 1;
    s.orders.push(lite);
    statsById.set(key, s);
  });

  const rows: DPPayrollRow[] = partnersSnap.docs.map(d => {
    const data = d.data() as any;
    const name = data.name || data.email || data.phone || d.id;
    const salary = num(data.salary);
    const altId = data.partnerId ? String(data.partnerId) : '';
    const s = statsById.get(d.id) || (altId ? statsById.get(altId) : undefined) || { count: 0, surge: 0, night: 0, orders: [] };
    const nightBonus = s.night * NIGHT_BONUS_PER_ORDER;
    const payable = salary + s.surge + nightBonus;
    const paidSoFar = (paidMap.get(d.id) || 0) + (altId ? (paidMap.get(altId) || 0) : 0);
    const remaining = Math.max(0, payable - paidSoFar);
    const isPaid = payable > 0 && remaining <= 0.5;
    return {
      entityId: d.id, name, salary, deliveredCount: s.count, surge: s.surge,
      nightCount: s.night, nightBonus, payable, paidSoFar, remaining, isPaid,
      orders: s.orders,
    };
  });

  rows.sort((a, b) => b.remaining - a.remaining || b.payable - a.payable);
  return rows;
}

// ── Transactions ─────────────────────────────────────────────────
export async function getMonthTransactions(month: number, year: number): Promise<Txn[]> {
  const q = query(
    collection(db, TXN_COLLECTION),
    where('periodMonth', '==', month),
    where('periodYear', '==', year),
  );
  const snap = await getDocs(q);
  const txns = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Txn[];
  txns.sort((a, b) => {
    const ta = toDate(a.paidAt)?.getTime() || 0;
    const tb = toDate(b.paidAt)?.getTime() || 0;
    return tb - ta;
  });
  return txns;
}

export async function recordPayout(args: RecordPayoutArgs): Promise<string> {
  const ref = await addDoc(collection(db, TXN_COLLECTION), {
    type: args.type,
    entityId: args.entityId,
    entityName: args.entityName,
    // Mirror onto restaurant*/partner* fields for cross-compatibility.
    restaurantId: args.type === 'restaurant' ? args.entityId : null,
    restaurantName: args.entityName,
    partnerId: args.type === 'partner' ? args.entityId : null,
    partnerName: args.type === 'partner' ? args.entityName : null,
    amount: args.amount,
    gross: args.gross ?? null,
    deductions: args.deductions ?? null,
    orderCount: args.orderCount ?? null,
    periodMonth: args.periodMonth,
    periodYear: args.periodYear,
    paidAt: serverTimestamp(),
    status: 'PAID',
    referenceId: args.referenceId ?? null,
    paymentMethod: args.paymentMethod ?? null,
    notes: args.notes ?? null,
  });
  return ref.id;
}
